import { useEffect, useMemo, useState } from "react";
import {
  Col,
  Row,
  Input,
  Label,
  FormFeedback,
  Form,
  CardBody,
  Card,
  Container,
  CardHeader,
  CardFooter,
  Alert,
  Button,
} from "reactstrap";
import { useFormik } from "formik";

import schema, { visitLogCreateSchema } from "./schema";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import BreadCrumb from "components/common/BreadCrumb";
import { toast } from "react-toastify";
import { BackButton } from "components/common/Buttons";
import AppFlatpickr from "components/common/AppFlatpickr";
import moment from "moment";
import {
  createAmcVisit,
  getAmcVisitById,
  updateAmcVisit,
} from "features/AmcVisitLogs/services/AmcVisitService";
import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import { getAmc, getAmcOptions } from "features/Amc/services/AmcService";
import { AmcContractStatus } from "features/Amc/constants/amc-contract.constants";
import type { SelectOption } from "types/api.types";
import { AmcVisit } from "features/AmcVisitLogs/types/amc-visit.types";
import {
  AmcVisitKind,
  AmcVisitStatus,
  amcVisitKindLabelMap,
  amcVisitStatusMap,
} from "features/AmcVisitLogs/constants/amc-visit.constants";
import { getDocumentTitle } from "utils/helpers";
import StatusBadge from "components/common/StatusBadge";
const VisitLogForm = ({ isEdit = false }: { isEdit?: boolean }) => {
  const [record, setRecord] = useState<AmcVisit>();
  const [searchParams] = useSearchParams();
  const prefilledAmcId = useMemo(() => {
    const n = Number(searchParams.get("amcId"));
    return Number.isInteger(n) && n > 0 ? n : "";
  }, [searchParams]);
  const [amcDefaultOption, setAmcDefaultOption] = useState<
    SelectOption | undefined
  >();
  const { visitId: visitIdParam } = useParams<{ visitId: string }>();
  const navigate = useNavigate();

  /** Location + schedule stay editable before start; lock from IN_PROGRESS onward. */
  const isLocationScheduleLocked =
    record != null &&
    [
      AmcVisitStatus.IN_PROGRESS,
      AmcVisitStatus.COMPLETED,
      AmcVisitStatus.CANCELLED,
      AmcVisitStatus.MISSED,
    ].includes(record.status as AmcVisitStatus);

  /** Visit finished or voided — nothing editable. */
  const isTerminalStatus =
    record != null &&
    [
      AmcVisitStatus.COMPLETED,
      AmcVisitStatus.CANCELLED,
      AmcVisitStatus.MISSED,
    ].includes(record.status as AmcVisitStatus);

  document.title = getDocumentTitle(
    !isEdit ? "New visit log" : "Edit visit log",
  );

  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      amcId: !isEdit && prefilledAmcId !== "" ? prefilledAmcId : "",
      scheduledAt: record?.scheduledAt
        ? new Date(record.scheduledAt).toISOString().slice(0, 16)
        : "",
      address: record?.address ?? "",
      city: record?.city ?? "",
      state: record?.state ?? "",
      remarks: record?.remarks ?? "",
    },

    validationSchema: isEdit ? schema : visitLogCreateSchema,
    onSubmit: async (values) => {
      try {
        if (!isEdit) {
          const amcId = Number(values.amcId);
          if (!Number.isInteger(amcId) || amcId <= 0) {
            toast.error("Select an active AMC contract.");
            return;
          }
          const response = await createAmcVisit({
            amcId,
            scheduledAt: values.scheduledAt,
            address: values.address,
            city: values.city,
            state: values.state,
            remarks: values.remarks?.trim() ? values.remarks : null,
            visitKind: AmcVisitKind.CONTRACT,
          });
          if (response.success && response.data?.id) {
            toast.success(response.message);
            navigate(`/amc-management/visit-logs/${response.data.id}`);
            return;
          }
          return;
        }

        const vid = Number(visitIdParam);
        if (record == null || !Number.isInteger(vid) || vid <= 0) {
          toast.error("Visit is not loaded yet.");
          return;
        }

        const response = await updateAmcVisit(vid, {
          scheduledAt: isLocationScheduleLocked
            ? (record.scheduledAt ?? values.scheduledAt)
            : values.scheduledAt,
          address: values.address,
          city: values.city,
          state: values.state,
          remarks: values.remarks?.trim() ? values.remarks : null,
        });

        if (response.success) {
          toast.success(response.message);
          navigate(-1);
        }
      } catch (err) {
        toast.error(
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ?? "Could not save visit",
        );
      }
    },
  });

  useEffect(() => {
    if (isEdit) {
      setAmcDefaultOption(undefined);
      return;
    }
    const id = prefilledAmcId === "" ? NaN : Number(prefilledAmcId);
    if (!Number.isInteger(id) || id <= 0) {
      setAmcDefaultOption(undefined);
      return;
    }
    let cancelled = false;
    void getAmc(id)
      .then((res) => {
        if (cancelled || !res.success || res.data?.id == null) return;
        const d = res.data;
        setAmcDefaultOption({
          value: d.id,
          label: d.code?.trim() ? d.code : `Contract #${d.id}`,
        });
      })
      .catch(() => {
        if (!cancelled) setAmcDefaultOption(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [isEdit, prefilledAmcId]);

  useEffect(() => {
    const fetchRecord = async () => {
      try {
        const vid = Number(visitIdParam);
        if (!Number.isInteger(vid) || vid <= 0) {
          return;
        }
        const response = await getAmcVisitById(vid);
        const { success, data } = response;
        if (success) {
          if (data.status === AmcVisitStatus.CANCELLED) {
            toast.info(
              "This AMC visit has been cancelled and cannot be edited",
            );
            navigate(-1);
            return;
          }
          setRecord(data);
        }
      } catch (err) {
        console.warn(err);
      }
    };

    if (isEdit) {
      void fetchRecord();
    }
  }, [visitIdParam, isEdit, navigate]);

  const breadcrumbItems =
    record != null
      ? [
          {
            label: "Visit logs",
            path: `/amc-management/visit-logs?amcId=${record.amcId}`,
          },
        ]
      : [
          {
            label: "Visit logs",
            path:
              prefilledAmcId !== ""
                ? `/amc-management/visit-logs?amcId=${prefilledAmcId}`
                : "/amc-management/visit-logs",
          },
        ];

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={isEdit ? "Edit visit log" : "New visit log"}
          pageTitle="AMC Management"
          items={breadcrumbItems}
        />
        <Form
          className="needs-validation"
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}
        >
          <Row className="justify-content-center">
            <Col>
              <Card className="shadow-none border">
                <CardHeader className="border-bottom bg-transparent pb-3">
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                    <h5 className="card-title mb-0">
                      {isEdit ? "Edit visit" : "Log visit"}
                    </h5>
                    {record ? (
                      <StatusBadge
                        value={record.status}
                        map={amcVisitStatusMap}
                      />
                    ) : null}
                  </div>
                  <p className="text-muted mb-0 small">
                    Set when the visit happens, where it takes place, and any
                    notes for your team.
                  </p>
                </CardHeader>
                <CardBody className="pt-4">
                  {!isEdit ? (
                    <>
                      <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                        AMC contract
                      </div>
                      <Row className="g-3 mb-4">
                        <Col md={8} lg={6}>
                          <Label className="form-label">
                            Contract{" "}
                            <span className="text-muted fw-normal">
                              (active only)
                            </span>
                          </Label>
                          <AsyncPaginatedSelect
                            fetchOptions={getAmcOptions}
                            requestParams={{
                              status: AmcContractStatus.ACTIVE,
                            }}
                            defaultOption={amcDefaultOption}
                            placeholder="Search active AMC contracts…"
                            value={
                              validation.values.amcId === ""
                                ? ""
                                : validation.values.amcId
                            }
                            onChange={(opt) => {
                              const val = Array.isArray(opt)
                                ? (opt[0] ?? null)
                                : opt;
                              const v = val?.value;
                              validation.setFieldValue(
                                "amcId",
                                typeof v === "number" &&
                                  Number.isFinite(v) &&
                                  v > 0
                                  ? v
                                  : "",
                              );
                            }}
                            onBlur={() =>
                              validation.setFieldTouched("amcId", true)
                            }
                            isInvalid={
                              validation.touched.amcId &&
                              Boolean(validation.errors.amcId)
                            }
                          />
                          {validation.touched.amcId &&
                          validation.errors.amcId ? (
                            <FormFeedback type="invalid" className="d-block">
                              {String(validation.errors.amcId)}
                            </FormFeedback>
                          ) : null}
                        </Col>
                      </Row>
                      <hr className="my-4 text-muted opacity-25" />
                    </>
                  ) : record ? (
                    <>
                      <div className="text-uppercase text-muted fw-semibold fs-12 mb-2">
                        AMC contract
                      </div>
                      <p className="mb-2">
                        <Link
                          to={`/amc-management/${record.amcId}`}
                          className="link fw-medium"
                        >
                          {record.contract?.code?.trim()
                            ? record.contract.code
                            : `Contract #${record.amcId}`}
                        </Link>
                      </p>
                      <p className="text-muted small mb-4">
                        Visit kind:{" "}
                        <span className="fw-medium text-body">
                          {record.visitKind
                            ? (amcVisitKindLabelMap[record.visitKind] ??
                              record.visitKind)
                            : "Contract visit"}
                        </span>
                      </p>
                      <hr className="my-4 text-muted opacity-25" />
                    </>
                  ) : null}
                  {record &&
                  isLocationScheduleLocked &&
                  !isTerminalStatus &&
                  record.status === AmcVisitStatus.IN_PROGRESS ? (
                    <Alert color="info" className="mb-4" role="status">
                      <i className="ri-map-pin-line me-2 align-middle" />
                      Location and schedule are locked while the visit is in
                      progress. You can still update remarks.
                    </Alert>
                  ) : null}
                  {record && isTerminalStatus ? (
                    <Alert color="warning" className="mb-4" role="alert">
                      <i className="ri-information-line me-2 align-middle" />
                      This visit is no longer active and cannot be edited.
                    </Alert>
                  ) : null}

                  <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                    Schedule
                  </div>
                  <Row className="g-3 mb-2">
                    <Col md={6} lg={5}>
                      <Label className="form-label" for="visit-scheduled-date">
                        Scheduled date
                      </Label>
                      <AppFlatpickr
                        id="visit-scheduled-date"
                        name="scheduledAt"
                        className={`form-control ${
                          validation.errors.scheduledAt &&
                          validation.touched.scheduledAt
                            ? "is-invalid"
                            : ""
                        }`}
                        placeholder="Select date"
                        options={{
                          altInput: true,
                          altFormat: "d M, Y",
                          dateFormat: "d M, Y",
                        }}
                        onChange={(date: any) =>
                          validation.setFieldValue(
                            "scheduledAt",
                            moment(date[0]).format("DD MMMM ,YYYY"),
                          )
                        }
                        value={
                          validation.values.scheduledAt
                            ? new Date(validation.values.scheduledAt)
                            : ""
                        }
                        disabled={isLocationScheduleLocked || isTerminalStatus}
                      />
                      {validation.touched.scheduledAt &&
                      validation.errors.scheduledAt ? (
                        <FormFeedback type="invalid" className="d-block">
                          {validation.errors.scheduledAt}
                        </FormFeedback>
                      ) : null}
                    </Col>
                  </Row>

                  <hr className="my-4 text-muted opacity-25" />

                  <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                    Site location
                  </div>
                  <Row className="g-3 mb-2">
                    <Col xs={12}>
                      <Label className="form-label" for="visit-address">
                        Address
                      </Label>
                      <Input
                        id="visit-address"
                        name="address"
                        className="form-control"
                        placeholder="Street, building, landmark…"
                        type="textarea"
                        rows={3}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.address || ""}
                        invalid={
                          validation.touched.address &&
                          validation.errors.address
                            ? true
                            : false
                        }
                        disabled={isLocationScheduleLocked || isTerminalStatus}
                      />
                      {validation.touched.address &&
                      validation.errors.address ? (
                        <FormFeedback type="invalid">
                          {validation.errors.address}
                        </FormFeedback>
                      ) : null}
                    </Col>
                    <Col md={6}>
                      <Label className="form-label" for="visit-city">
                        City
                      </Label>
                      <Input
                        id="visit-city"
                        name="city"
                        className="form-control"
                        placeholder="City"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.city || ""}
                        invalid={
                          validation.touched.city && validation.errors.city
                            ? true
                            : false
                        }
                        disabled={isLocationScheduleLocked || isTerminalStatus}
                      />
                      {validation.touched.city && validation.errors.city ? (
                        <FormFeedback type="invalid">
                          {validation.errors.city}
                        </FormFeedback>
                      ) : null}
                    </Col>
                    <Col md={6}>
                      <Label className="form-label" for="visit-state">
                        State
                      </Label>
                      <Input
                        id="visit-state"
                        name="state"
                        className="form-control"
                        placeholder="State"
                        type="text"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.state || ""}
                        invalid={
                          validation.touched.state && validation.errors.state
                            ? true
                            : false
                        }
                        disabled={isLocationScheduleLocked || isTerminalStatus}
                      />
                      {validation.touched.state && validation.errors.state ? (
                        <FormFeedback type="invalid">
                          {validation.errors.state}
                        </FormFeedback>
                      ) : null}
                    </Col>
                  </Row>

                  <hr className="my-4 text-muted opacity-25" />

                  <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                    Remarks
                  </div>
                  <Row>
                    <Col>
                      <Label
                        className="form-label visually-hidden"
                        for="visit-remarks"
                      >
                        Remarks
                      </Label>
                      <Input
                        id="visit-remarks"
                        className="form-control"
                        type="textarea"
                        name="remarks"
                        placeholder="Optional notes for this visit…"
                        rows={3}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.remarks || ""}
                        invalid={
                          validation.touched.remarks &&
                          validation.errors.remarks
                            ? true
                            : false
                        }
                        disabled={isTerminalStatus}
                      />
                      {validation.touched.remarks &&
                      validation.errors.remarks ? (
                        <FormFeedback type="invalid">
                          {validation.errors.remarks}
                        </FormFeedback>
                      ) : null}
                    </Col>
                  </Row>
                </CardBody>
                <CardFooter className="bg-light bg-opacity-50 border-top d-flex flex-wrap justify-content-between align-items-center gap-2 py-3">
                  <BackButton />
                  <Button
                    type="submit"
                    color="primary"
                    className="btn-label px-4"
                    disabled={isTerminalStatus || validation.isSubmitting}
                  >
                    {validation.isSubmitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        {isEdit ? "Updating…" : "Submitting…"}
                      </>
                    ) : isEdit ? (
                      "Save changes"
                    ) : (
                      "Submit visit"
                    )}
                  </Button>
                </CardFooter>
              </Card>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

export default VisitLogForm;
